import { reactive, computed } from "vue";
import { getStudentClassrooms } from "@/utils/services";

/**
 * Reporting provider du module Classroom.
 *
 * Deux sources, toutes deux réelles :
 *  - /api/grades  → indicateurs financiers et effectifs (déjà chargé par la
 *    liste des classes, donc gratuit) ;
 *  - /api/get_classroom_details → élèves de chaque classe, dont le champ
 *    `gender` ("M" / "F") saisi à la création de l'élève. C'est lui qui donne
 *    le partage garçons / filles.
 *
 * Aucune estimation, aucun compteur incrémental maintenu à la main : à chaque
 * chargement de la liste on relit les données et on recompte. Un ajout ou une
 * suppression d'élève se reflète donc automatiquement, sans risque de double
 * comptage ni de dérive entre l'écran et la base.
 */

const state = reactive({
  boys: 0,
  girls: 0,
  overdue: 0,
  pending_amount: 0,
  paid_amount: 0,
  total_students: 0,
  /** Vrai pendant le comptage des genres (les autres cartes sont déjà à jour). */
  gender_loading: false,
});

/** Additionne un champ numérique sur toutes les classes de tous les niveaux. */
function sumOverClassrooms(schoolList, reader) {
  let total = 0;
  (schoolList || []).forEach((grade) => {
    (grade.classrooms || []).forEach((classroom) => {
      total += reader(classroom);
    });
  });
  return total;
}

/** Aplatit la réponse de /api/grades en une simple liste de classes. */
function flattenClassrooms(schoolList) {
  const classrooms = [];
  (schoolList || []).forEach((grade) => {
    (grade.classrooms || []).forEach((classroom) => classrooms.push(classroom));
  });
  return classrooms;
}

/**
 * Indicateurs disponibles immédiatement dans /api/grades.
 * Appelé par la liste des classes dès que les niveaux sont chargés.
 */
export function ingestClassrooms(schoolList) {
  state.total_students = sumOverClassrooms(
    schoolList,
    (c) => Number(c.student_count) || 0
  );
  state.overdue = sumOverClassrooms(schoolList, (c) => Number(c.overdue) || 0);
  state.pending_amount = sumOverClassrooms(
    schoolList,
    (c) => Number(c.pending_amount) || 0
  );
  state.paid_amount = sumOverClassrooms(
    schoolList,
    (c) => Number(c.settled_amount ?? c.paid_amount ?? 0) || 0
  );
}

/**
 * Compte les garçons et les filles à partir du champ `gender` des élèves.
 *
 * Les élèves ne sont pas renvoyés par /api/grades : il faut une requête par
 * classe. Elles sont donc lancées par lots pour ne pas saturer le réseau, et
 * le comptage se fait en arrière-plan — les cartes financières, elles, sont
 * déjà affichées.
 *
 * Une classe injoignable est ignorée plutôt que de faire échouer le total.
 */
export async function countGenders(schoolList) {
  const classrooms = flattenClassrooms(schoolList);
  if (!classrooms.length) {
    state.boys = 0;
    state.girls = 0;
    return;
  }

  state.gender_loading = true;
  let boys = 0;
  let girls = 0;
  const BATCH = 4;

  try {
    for (let i = 0; i < classrooms.length; i += BATCH) {
      const results = await Promise.allSettled(
        classrooms
          .slice(i, i + BATCH)
          .map((classroom) => getStudentClassrooms({ classroom_id: classroom.id }))
      );

      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const students = (result.value && result.value.data && result.value.data.students) || [];
        students.forEach((student) => {
          // Le formulaire de création enregistre "M" ou "F". On teste la
          // première lettre pour rester tolérant à "Masculin"/"Féminin" ou
          // à une casse différente. Un genre absent n'est compté nulle part
          // plutôt que d'être rattaché par défaut à l'un des deux.
          const value = String(student.gender || "").trim().toLowerCase();
          if (value.startsWith("f")) girls += 1;
          else if (value.startsWith("m")) boys += 1;
        });
      });
    }

    state.boys = boys;
    state.girls = girls;
  } catch (error) {
    console.error("Failed to count genders", error);
  } finally {
    state.gender_loading = false;
  }
}

export const classroomStats = state;

export const totalStudents = computed(() => state.total_students);
